import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, HardDrive } from "lucide-react";

const emptyRow = () => ({ device_name: "", serial_no: "", mac_address: "", notes: "" });

// Repeatable rows for the serial number / MAC address of every physical
// device installed on this job. Kept deliberately separate from
// LineItemsEditor (invoices/sales) -- there's no price, quantity, VAT or
// stock concept here, just "what, with what serial, went in the ground."
// Synced server-side into the standalone device_installs registry on save
// (see backend/device_sync.py) so a serial stays traceable system-wide.
export function DevicesEditor({ value, onChange }) {
  const [rows, setRows] = useState(() => (Array.isArray(value?.devices) && value.devices.length ? value.devices : []));

  useEffect(() => {
    onChange({ devices: rows });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const updateRow = (i, patch) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => setRows((rs) => [...rs, emptyRow()]);
  const removeRow = (i) => setRows((rs) => rs.filter((_, idx) => idx !== i));

  return (
    <div className="md:col-span-2 space-y-3 border-t border-border pt-4 mt-2">
      <div className="flex items-center justify-between">
        <Label className="text-xs uppercase tracking-[0.16em] flex items-center gap-1.5">
          <HardDrive className="w-3.5 h-3.5" /> Devices Installed
        </Label>
        <Button type="button" size="sm" variant="outline" onClick={addRow} data-testid="add-device-row">
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Device
        </Button>
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground border border-dashed border-border rounded-lg p-4 text-center">
          No devices added yet. Record each device's serial number (and MAC address, if applicable) as you install it —
          the customer's completion certificate is generated from this list.
        </p>
      ) : (
        <div className="space-y-2">
          {rows.map((row, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-[1.2fr_1fr_1fr_1fr_auto] gap-2 items-start p-3 rounded-lg border border-border bg-muted/20">
              <Input placeholder="Device / item (e.g. Dome Camera 4MP)" value={row.device_name} onChange={(e) => updateRow(i, { device_name: e.target.value })} data-testid={`device-name-${i}`} />
              <Input placeholder="Serial number" value={row.serial_no} onChange={(e) => updateRow(i, { serial_no: e.target.value })} data-testid={`device-serial-${i}`} />
              <Input placeholder="MAC address (optional)" value={row.mac_address} onChange={(e) => updateRow(i, { mac_address: e.target.value })} data-testid={`device-mac-${i}`} />
              <Input placeholder="Notes (optional)" value={row.notes} onChange={(e) => updateRow(i, { notes: e.target.value })} data-testid={`device-notes-${i}`} />
              <Button type="button" size="icon" variant="ghost" onClick={() => removeRow(i)} className="hover:text-destructive" data-testid={`remove-device-${i}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
